import { GCodeCommand, GCodeCommandParam } from "./GCodeCommand"
import { AbstractCommand } from "./AbstractCommand"
import type { NumberRange } from "src/types"
import { isUpdateTempCommand } from "src/responses"

// Temperature safety limits
const TEMP_LIMITS = {
    NOZZLE: { MIN: 20, MAX: 300 },
    BED: { MIN: 20, MAX: 120 },
    CHAMBER: { MIN: 20, MAX: 60 }
} as const

export enum TempUpdatePartType {
    BED = "M140",
    EXTRUDER = "M104"
}

/**
 * Validates temperature is within safe range for the given part
 * @throws Error if temperature is outside safe range
 */
function validateTemperature(part: TempUpdatePartType | "CHAMBER", temperature: number): void {
    let limits: typeof TEMP_LIMITS[keyof typeof TEMP_LIMITS]

    switch (part) {
        case TempUpdatePartType.EXTRUDER:
            limits = TEMP_LIMITS.NOZZLE
            break
        case TempUpdatePartType.BED:
            limits = TEMP_LIMITS.BED
            break
        case "CHAMBER":
            limits = TEMP_LIMITS.CHAMBER
            break
        default:
            throw new Error(`Unknown part type: ${part}`)
    }

    if (temperature < limits.MIN || temperature > limits.MAX) {
        throw new Error(
            `Temperature ${temperature}°C is outside safe range (${limits.MIN}°C - ${limits.MAX}°C) for ${part}`
        )
    }
}

export class UpdateTempCommand extends GCodeCommand {
    public command: GCodeCommandParam = "gcode_line"

    /**
     * Constructs a command to change the target temperatures of bed or extruder.
     * @param part The part to change the target temperature of.
     * @param temperature The new target temperature in Celsius.
     * @throws Error if temperature is outside safe range
     */
    public constructor({
        part,
        temperature,
    }: {
        part: TempUpdatePartType
        temperature: NumberRange<0, 300>
    }) {
        validateTemperature(part, temperature)
        super([`${part} S${temperature}`])
    }

    ownsResponse = isUpdateTempCommand
}

export class UpdateChamberTempCommand extends AbstractCommand {
    public category: "info" | "pushing" | "system" | "print" = "print"
    public command = "set_ctt"

    /**
     * Constructs a command to change the target temperature of the chamber.
     * @param temperature The new target temperature in Celsius.
     * @throws Error if temperature is outside safe range
     */
    public constructor(temperature: NumberRange<0, 300>) {
        validateTemperature("CHAMBER", temperature)
        super({ ctt_val: temperature })
    }

    ownsResponse = isUpdateTempCommand
}